import { motion } from 'framer-motion';
import { ArrowRight, Info, ChevronDown } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { useState } from 'react';
import { Link } from 'react-router-dom';

const CoreComponent = ({ icon, title, description, realWorldValidation }: { 
  icon: string; 
  title: string; 
  description: string;
  realWorldValidation: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.8 }}
    whileHover={{ scale: 1.02 }}
    className="bg-indigo-950/30 p-8 rounded-xl backdrop-blur-sm border border-indigo-500/20"
  >
    <div className="flex justify-center mb-6">
      <span className="text-4xl">{icon}</span>
    </div>
    <h3 className="text-2xl font-semibold mb-4 text-center text-indigo-400">{title}</h3>
    <p className="text-gray-300 text-center mb-6">{description}</p>
    <div className="h-px bg-indigo-500/20 mb-6" />
    <p className="text-gray-400 text-center text-sm">{realWorldValidation}</p>
  </motion.div>
);

const JourneyStep = ({ icon, label, isLast = false }: { icon: string; label: string; isLast?: boolean }) => (
  <div className="flex items-center">
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center"
    >
      <div className="w-16 h-16 flex items-center justify-center bg-indigo-950/30 rounded-full mb-2 backdrop-blur-sm border border-indigo-500/20">
        <span className="text-2xl">{icon}</span>
      </div>
      <span className="text-sm text-gray-300 text-center">{label}</span>
    </motion.div>
    {!isLast && (
      <motion.div 
        initial={{ opacity: 0, width: 0 }}
        whileInView={{ opacity: 1, width: "100px" }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, delay: 0.2 }}
        className="h-px bg-gradient-to-r from-indigo-500/50 to-purple-500/50 mx-4"
      />
    )}
  </div>
);

interface TooltipProps {
  text: string;
  isVisible: boolean;
}

const Tooltip = ({ text, isVisible }: TooltipProps) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 10 }}
    transition={{ duration: 0.2 }}
    className="absolute -top-16 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/90 backdrop-blur-sm rounded-lg border border-indigo-500/20 shadow-[0_0_10px_rgba(99,102,241,0.2)] w-48 z-50"
  >
    <div className="text-gray-200 text-xs text-center">{text}</div>
    <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-black/90 border-r border-b border-indigo-500/20 transform rotate-45"></div>
  </motion.div>
);

const ExampleCard = ({ title, description, isOffset = false }: { 
  title: string; 
  description: string;
  isOffset?: boolean;
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      whileHover={{ scale: 1.02 }}
      className={`bg-indigo-950/30 p-8 rounded-xl backdrop-blur-sm border border-indigo-500/20 ${isOffset ? 'md:translate-y-6' : ''}`}
      onTouchStart={() => setShowTooltip(true)}
      onTouchEnd={() => setShowTooltip(false)}
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      <h3 className="text-xl font-semibold mb-4 text-indigo-400">{title}</h3>
      <p className="text-gray-300">{description}</p>
    </motion.div>
  );
};

const HarmonySignalEnginePage = () => {
  const coreComponents = [
    {
      icon: "📡",
      title: "Signal Mesh Connectivity",
      description: "Receive task signals via satellite and local decentralized mesh — no carriers, no borders.",
      realWorldValidation: "Powered by partnerships with global satellite providers and encrypted local mesh nodes, the Harmony Network routes task signals directly to your device, bypassing telecom infrastructure entirely."
    },
    {
      icon: "🤖",
      title: "Harmony Agent Suites",
      description: "AI agents execute tasks across CTO, CMO, CFO, and operational domains automatically.",
      realWorldValidation: "Harmony Agents are funded by real businesses seeking rapid task execution. Each completed task is linked to cash-backed pools, triggering ESSENCE rewards that are tied to real demand, not speculation."
    },
    {
      icon: "🔒",
      title: "OmniSeal Security",
      description: "Pure Cipher's zero-knowledge encryption locks every signal, task, and reward at the device layer.",
      realWorldValidation: "OmniSeal is a proprietary zero-knowledge encryption protocol audited by PureCipher, ensuring no entity, including SELF Labs, can access, alter, or monetize your signal or rewards without your permission."
    },
    {
      icon: "🪙",
      title: "ESSENCE Reward Layer",
      description: "Completing tasks generates ESSENCE — a stable-value token you control, earn, and spend freely.",
      realWorldValidation: "ESSENCE is backed by businesses funding task pools with fiat and stable assets. Users can convert ESSENCE into cash, spend it inside the AI App Store, or hold it as a stable reserve inside the growing Signal Economy."
    }
  ];

  const journeySteps = [
    { icon: "📡", label: "Signal Received" },
    { icon: "🤖", label: "Agent Executes Task" },
    { icon: "🪙", label: "ESSENCE Earned" },
    { icon: "💵", label: "Cash Out or Spend" }
  ];

  const basicExamples = [
    {
      title: "Customer Service Tasks",
      description: "Businesses use Harmony Agents to answer basic customer inquiries — your phone executes microtasks and you earn ESSENCE."
    },
    {
      title: "Content Moderation",
      description: "Harmony Agents scan posts for AI moderation jobs — if your phone receives and completes the scan signal, you earn."
    },
    {
      title: "Smart Data Validation",
      description: "Agents verify business data (like store hours or pricing) — completed verifications trigger ESSENCE rewards."
    }
  ];

  const enterpriseExamples = [
    {
      title: "CTO-Level System Generations",
      description: "Harmony Agents dynamically generate network health reports, security audits, and infrastructure blueprints. Completed generations automatically trigger ESSENCE earnings."
    },
    {
      title: "CMO-Level Marketing Executions",
      description: "Harmony Agents create A/B marketing assets, auto-generate campaign reports, and optimize SEO strategies. When executions are finalized, ESSENCE is rewarded."
    },
    {
      title: "CFO-Level Financial Validations",
      description: "Harmony Agents auto-generate transaction summaries, validate ledger integrity, and produce compliance checklists. Successful validations result in ESSENCE payouts."
    }
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      <Helmet>
        <title>Harmony Signal Engine - SELF Labs</title>
        <meta name="description" content="Discover how the Harmony Signal Engine powers the Signal Economy through AI task execution, secure communications, and automated earnings." />
        
        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Harmony Signal Engine - SELF Labs" />
        <meta property="og:description" content="Discover how the Harmony Signal Engine powers the Signal Economy through AI task execution, secure communications, and automated earnings." />
        <meta property="og:image" content="/assets/images/Phone in box.png" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Harmony Signal Engine - SELF Labs" />
        <meta name="twitter:description" content="Discover how the Harmony Signal Engine powers the Signal Economy through AI task execution, secure communications, and automated earnings." />
        <meta name="twitter:image" content="/assets/images/Phone in box.png" />
      </Helmet>

      {/* Background Effects */}
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.1)_0%,transparent_100%)]" />
      <div className="fixed inset-0 bg-[radial-gradient(circle_at_center,rgba(138,43,226,0.03)_0%,transparent_1px)] bg-[length:24px_24px]" />

      <div className="relative">
        {/* Hero Section */}
        <section className="relative py-32 overflow-hidden">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
                The Harmony Signal Engine: How the SELF Phone Powers the Signal Economy
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 leading-relaxed">
                At the heart of every SELF Phone is the Harmony Signal Engine — a revolutionary system where embedded signal metadata, decentralized communication, AI task execution, and encrypted earnings combine to create a sovereign economy owned by you.
              </p>
            </motion.div>

            {/* Scroll Indicator */}
            <motion.div 
              className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
              animate={{ 
                y: [0, 10, 0],
                opacity: [0.3, 1, 0.3],
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <span className="text-indigo-400 text-sm mb-2 md:hidden">Scroll to explore</span>
              <ChevronDown className="w-8 h-8 text-indigo-400" />
            </motion.div>
          </div>
        </section>

        {/* Core Components */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {coreComponents.map((component, index) => (
                <CoreComponent
                  key={`component-${index}`}
                  icon={component.icon}
                  title={component.title}
                  description={component.description}
                  realWorldValidation={component.realWorldValidation}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Execution Journey */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto overflow-x-auto relative">
              {/* Left scroll indicator */}
              <div className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 bg-gradient-to-r from-black via-black/80 to-transparent px-4 py-2 z-10">
                <motion.span 
                  className="text-indigo-400 text-sm whitespace-nowrap flex items-center"
                  animate={{ 
                    x: [-5, 5, -5],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <ArrowRight className="w-4 h-4 rotate-180 mr-1" />
                  Scroll
                </motion.span>
              </div>
              
              {/* Right scroll indicator */}
              <div className="md:hidden absolute right-0 top-1/2 -translate-y-1/2 bg-gradient-to-l from-black via-black/80 to-transparent px-4 py-2 z-10">
                <motion.span 
                  className="text-indigo-400 text-sm whitespace-nowrap flex items-center"
                  animate={{ 
                    x: [-5, 5, -5],
                    opacity: [0.3, 1, 0.3],
                  }}
                  transition={{ 
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  Scroll
                  <ArrowRight className="w-4 h-4 ml-1" />
                </motion.span>
              </div>

              <div className="flex justify-center min-w-max py-8">
                {journeySteps.map((step, index) => (
                  <motion.div
                    key={`step-${index}`}
                    className="flex items-center"
                    animate={{ 
                      boxShadow: ['0 0 0 rgba(99,102,241,0)', '0 0 20px rgba(99,102,241,0.3)', '0 0 0 rgba(99,102,241,0)']
                    }}
                    transition={{ 
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.5
                    }}
                  >
                    <JourneyStep
                      icon={step.icon}
                      label={step.label}
                      isLast={index === journeySteps.length - 1}
                    />
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Real Value Section */}
        <section className="py-20 bg-indigo-950/20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
                Why ESSENCE Is Real Value — Not Monopoly Money
              </h2>
              <p className="text-xl text-gray-300 leading-relaxed mb-6">
                ESSENCE represents the direct value of AI-led Generations and Executions funded by real-world businesses. These are not speculative tokens. Every Harmony signal processed translates into tangible enterprise outputs, secured by fiat and stablecoin-backed task pools. You're not just earning from clicks — you're participating in sovereign, AI-driven economy formation.
              </p>
              <p className="text-xl text-gray-300 leading-relaxed">
                ESSENCE can be converted to cash, used to upgrade your SELF Phone, or spent inside the Role Model AI Store to access premium AI agents, applications, and services — all with full transparency and sovereignty.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Examples Section */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold mb-8 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
                Examples of Enterprise AI Generations & Executions
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-8">
              {enterpriseExamples.map((example, index) => (
                <ExampleCard
                  key={`enterprise-example-${index}`}
                  title={example.title}
                  description={example.description}
                  isOffset={index % 2 === 1}
                />
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="text-sm text-gray-400 text-center italic max-w-3xl mx-auto"
            >
              Examples shown are simplified representations. Real-world Generations and Executions vary by enterprise integration.
            </motion.p>
          </div>
        </section>

        {/* Cash Pools Section */}
        <section className="py-20 bg-indigo-950/20">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-2xl md:text-3xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-indigo-400 via-purple-400 to-blue-400">
                Built on Real Cash Pools — Not Speculative Hype
              </h2>
              <p className="text-lg text-gray-300 leading-relaxed mb-6">
                Every Harmony task completed on your SELF Phone is funded by businesses depositing fiat or stablecoins into secured pools. This creates direct, real-time value transfer — meaning you're earning from actual economic activity, not future promises.
              </p>
              <p className="text-lg text-gray-300 leading-relaxed">
                Every Generation and Execution processed by a SELF Phone ties directly into business-verified funding pools. ESSENCE is not just compensation for small microtasks — it's a reward for enabling automated enterprise functions across technology, marketing, finance, and operations.
              </p>
            </motion.div>
          </div>
        </section>

        {/* White Paper CTA */}
        <section className="py-20">
          <div className="container mx-auto px-4 text-center">
            <Link
              to="/harmony-litepaper"
              className="inline-block mt-8 px-8 py-4 bg-indigo-600 hover:bg-indigo-700 rounded-xl text-white font-semibold transition-colors duration-200"
            >
              Dive Deeper: Read the Full Harmony Technical Litepaper →
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HarmonySignalEnginePage; 